import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Smartphone, Tablet } from 'lucide-react';
import QRCode from 'qrcode';
import { AppsResponse } from '@cloudsign/shared';
import type { AppDownload } from '@cloudsign/shared';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { ErrorPanel } from '../components/ErrorPanel';
import { EmptyState, PageHeader, Spinner } from '../components/ui';

/** Draws the download link as a QR code in the browser (no request is made). */
function Qr({ url, name }: { url: string; name: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { margin: 1, width: 192 })
      .then((d) => {
        if (!cancelled) setSrc(d);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);
  if (!src) return <div className="cs-skeleton h-48 w-48" aria-hidden="true" />;
  return <img src={src} width={192} height={192} alt={t('apps.qr.alt', { name })} className="rounded-md border border-slate-200 bg-white" />;
}

function AppCard({ app }: { app: AppDownload }) {
  const name = t(`apps.${app.key}.name`);
  const Icon = app.key === 'kiosk' ? Tablet : Smartphone;
  return (
    <article aria-labelledby={`app-${app.key}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-4">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
          <Icon aria-hidden="true" className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={`app-${app.key}`} className="font-semibold text-slate-900">
            {name}
          </h2>
          <p className="mt-0.5 text-sm text-slate-600">{t(`apps.${app.key}.purpose`)}</p>
          {app.version ? <p className="mt-1 text-sm text-slate-600">{t('apps.version', { version: app.version })}</p> : null}
        </div>
      </div>
      <div className="mt-4 flex flex-col items-start gap-4 sm:flex-row">
        <figure className="shrink-0">
          <Qr url={app.url} name={name} />
          <figcaption className="mt-1 max-w-48 text-xs text-slate-600">{t('apps.qr.help')}</figcaption>
        </figure>
        <div className="min-w-0 flex-1 space-y-3">
          <a
            href={app.url}
            rel="noreferrer noopener"
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-base font-medium text-on-brand hover:bg-brand-hover sm:w-auto"
          >
            <Download aria-hidden="true" className="h-4 w-4" />
            {t('apps.download', { name })}
          </a>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{t('apps.steps.title')}</h3>
            <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm text-slate-700">
              <li>{t('apps.steps.open')}</li>
              <li>{t('apps.steps.allow')}</li>
              <li>{t(`apps.steps.${app.key}`)}</li>
            </ol>
          </div>
        </div>
      </div>
    </article>
  );
}

/** /apps: the Android apps the platform has published for download. Open to every signed-in tenant user. */
export function AppsPage() {
  const q = useQuery({ queryKey: ['apps'], queryFn: () => api.get('/apps', { schema: AppsResponse }) });
  return (
    <>
      <PageHeader title={t('apps.title')} description={t('apps.description')} />
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : q.data.apps.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <EmptyState message={<><strong className="block text-slate-900">{t('apps.empty.title')}</strong>{t('apps.empty.body')}</>} />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {q.data.apps.map((a) => (
            <AppCard key={a.key} app={a} />
          ))}
        </div>
      )}
    </>
  );
}
